import {Injectable} from '@angular/core';
import {forkJoin, from, Observable} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {readCollectionWithIds, readDocumentWithId} from '../common/firestore-utils';
import {TenantService} from './tenant.service';
import {User} from '../models/user.model';
import uniq from 'uniq';
import {Timestamp} from '@firebase/firestore';
import { Firestore, QueryConstraint, collection, collectionData, doc, docData, getDoc, limit, orderBy, query, setDoc, startAfter, updateDoc } from '@angular/fire/firestore';


@Injectable({
  providedIn: 'root'
})
export class SchoolUsersDbService {

  constructor(
    private firestore: Firestore,
    private tenant: TenantService) {

  }

  loadUsersPageByEmail(pageSize: number, startAfterEmail?: string): Observable<User[]> {
    const constraints: QueryConstraint[] = [
      orderBy('email', 'asc'),
      limit(pageSize),
    ]

    if(startAfterEmail) {
      constraints.push(startAfter(startAfterEmail))
    }

    const usersQuery = query(
      collection(this.firestore, `schools/${this.tenant.id}/users`),
      ...constraints
    )

    return collectionData(usersQuery, {idField: 'id'}).pipe(first()) as Observable<User[]>;

  }

  async saveUserProfileIfNeeded(tenantId:string, userId: string, email: string, pictureUrl: string, displayName: string) {

    const userPath = `schools/${tenantId}/users/${userId}`

    console.log(`Checking if profile of user ${userId} needs to be saved: ${userPath} `);

    const user = doc(this.firestore, userPath);

    const userData = await docData(user, {idField: 'id'}).pipe(first()).toPromise() as User;

    console.log(`returned user data: ${JSON.stringify(userData)}`);

    if (userData?.email) {
      console.log(`User with email found, skipping profile save.`);
      return null;
    }

    console.log(`User with email not found, creating new user profile.`);

    return setDoc(doc(this.firestore, userPath), {
      email,
      pictureUrl: pictureUrl || null,
      displayName: displayName || null,
    }, {merge: true})

  }

  findUserByUid(uid: string): Observable<User> {

    const usersRef = doc(this.firestore, `schools/${this.tenant.id}/users/${uid}`)
    const usersPrivateRef = doc(this.firestore, `schools/${this.tenant.id}/usersPrivate/${uid}`)

    const usersRefPromise = getDoc(usersRef)
      .then((doc) => {
        return doc?.exists() ? doc.data() : undefined
      })

    const usersPrivateRefPromise = getDoc(usersPrivateRef)
      .then((doc) => {
        return doc?.exists() ? doc.data() : undefined
      })

    console.log(`PERFORMANCE called findUserByUid with ${uid}`);
    return forkJoin(
      [
        from(usersRefPromise),
        from(usersPrivateRefPromise)
      ])
      .pipe(
        map(([data, privateData]) => {
          return {
            ...data,
            ...privateData,
            id:uid
          };
        })
      );

    // return forkJoin(
    //   [
    //     readDocumentWithId<User>(docData(doc(this.firestore, `schools/${this.tenant.id}/users/${uid}`))).pipe(first()),
    //     readDocumentWithId<User>(docData(doc(this.firestore, `schools/${this.tenant.id}/usersPrivate/${uid}`))).pipe(first())
    //   ])
    //   .pipe(
    //     map(([data, privateData]) => {
    //       return {
    //         ...data,
    //         ...privateData,
    //         id:uid
    //       };
    //     })
    //   );
  }

  loadUserPrivate(tenantId: string, userId: string): Observable<{ purchasedCourses: string[], temporaryAccess?:any }> {
    console.log(`PERFORMANCE Called loadUserPrivate() with tenantId ${tenantId} and userId ${userId}`);
    return docData(doc(this.firestore, `schools/${tenantId}/usersPrivate/${userId}`))
      .pipe(
        map(snap => {

          console.log(`PERFORMANCE Loaded loadUserPrivate() user snapshot ${JSON.stringify(snap)}`, snap);


          if(!snap || !snap.purchasedCourses){
            return {
              purchasedCourses: [],
              temporaryAccess:{}
            };
          }
          const userPrivate = snap as User;
          if (userPrivate.purchasedCourses) {
            uniq(userPrivate.purchasedCourses);
          }
          return {
            purchasedCourses: userPrivate.purchasedCourses || [],
            temporaryAccess:userPrivate.temporaryAccess || {}
          };
        }),
        first()
      );
  }

  updateUser(userId: string, props: Partial<User>) {
    const path = `schools/${this.tenant.id}/users/${userId}`;
    console.log(`Updating user in path ${path}`);
    const updateUserAsync = setDoc(doc(this.firestore,path), props, {merge: true})
    return from(updateUserAsync);
  }

  updateUserByTenantAndId(tenantId: string, userId: string, props: Partial<User>) {
    const path = `schools/${tenantId}/users/${userId}`;
    console.log(`Updating user in path ${path}`);
    const updateUserAsync = setDoc(doc(this.firestore, path), props, {merge: true});
    return from(updateUserAsync);
  }

  updateUserPrivate(userId: string, props: any) {
    // this will only work for admins
    const updateUserAsync = setDoc(doc(this.firestore, `schools/${this.tenant.id}/usersPrivate/${userId}`), props, {merge: true})
    return from(updateUserAsync);
  }

  updateNewsletterStatus(userId: string, val: boolean) {
    const updateUserAsync = updateDoc(doc(this.firestore,`schools/${this.tenant.id}/users/${userId}`), {unsubscribeFromNewsletter: val});
    return from(updateUserAsync);
  }

  findUserByTenantAndId(tenantId: string, userId: string) {
    return readDocumentWithId<User>(docData(doc(this.firestore,`schools/${tenantId}/users/${userId}`))).pipe(first());
  }

  saveLastSeenAt(tenantId: string, userId:string) {

    const userPath = `schools/${tenantId}/users/${userId}`

    const updateUserAsync = setDoc(doc(this.firestore, userPath), {
      invitationStatus: "active",
      lastSeenAt: Timestamp.now()
    }, {merge: true})


    return updateUserAsync;
  }

}
